function newFontId() {
  return `font-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function quillFontKeyForCustomFont(id) {
  const safe = String(id || 'font').replace(/[^a-zA-Z0-9-]/g, '');
  return `wc-font-${safe}`;
}

export function customFontChoiceValue(id) {
  return `custom:${id}`;
}

export function parseCustomFontChoice(value) {
  const s = String(value || '');
  if (!s.startsWith('custom:')) return null;
  return s.slice(7) || null;
}

/** Normalise typography + migration legacy single font → bibliothèque. */
export function normalizeSiteTypography(typography) {
  const base = typography && typeof typography === 'object' ? { ...typography } : {};
  let customFonts = Array.isArray(base.customFonts)
    ? base.customFonts.filter((f) => f?.fontUrl && f?.fontFamily)
    : [];

  if (!customFonts.length && base.fontUrl && base.fontFamily) {
    customFonts = [
      {
        id: base.defaultFontId || newFontId(),
        label: base.fontFamily,
        fontFamily: base.fontFamily,
        fontUrl: base.fontUrl,
      },
    ];
  }

  customFonts = customFonts.map((f) => ({
    id: f.id || newFontId(),
    label: f.label || f.fontFamily || 'Police',
    fontFamily: String(f.fontFamily || '').trim(),
    fontUrl: f.fontUrl,
  }));

  const defaultFontId =
    base.defaultFontId && customFonts.some((f) => f.id === base.defaultFontId)
      ? base.defaultFontId
      : customFonts[0]?.id || null;

  const defaultFont = customFonts.find((f) => f.id === defaultFontId) || null;

  return {
    fontFamily: defaultFont?.fontFamily || base.fontFamily || '',
    fontUrl: defaultFont?.fontUrl || base.fontUrl || '',
    applyToSite: base.applyToSite === true,
    customFonts,
    defaultFontId,
  };
}

export function getSiteCustomFonts(typography) {
  return normalizeSiteTypography(typography).customFonts;
}

export function getSiteDefaultFont(typography) {
  const t = normalizeSiteTypography(typography);
  return t.customFonts.find((f) => f.id === t.defaultFontId) || t.customFonts[0] || null;
}

export function getCustomFontById(typography, fontId) {
  if (!fontId) return null;
  return getSiteCustomFonts(typography).find((f) => f.id === fontId) || null;
}

export function siteHasCustomFonts(typography) {
  return getSiteCustomFonts(typography).length > 0;
}

export function siteHasCustomFont(typography) {
  return siteHasCustomFonts(typography);
}

function cssFontFamilyName(name) {
  return `"${String(name || '').replace(/"/g, '\\"')}", system-ui, sans-serif`;
}

export function resolveFontFamilyChoice(choice, typography) {
  const customId = parseCustomFontChoice(choice);
  if (customId) {
    const rec = getCustomFontById(typography, customId);
    if (rec) return cssFontFamilyName(rec.fontFamily);
  }

  if (choice === 'site') {
    const def = getSiteDefaultFont(typography);
    if (def) return cssFontFamilyName(def.fontFamily);
    return 'system-ui, sans-serif';
  }

  const stacks = {
    system: 'system-ui, -apple-system, Segoe UI, sans-serif',
    serif: 'Georgia, "Times New Roman", serif',
    arial: 'Arial, Helvetica, sans-serif',
    georgia: 'Georgia, serif',
    'times-new-roman': '"Times New Roman", Times, serif',
    verdana: 'Verdana, Geneva, sans-serif',
    tahoma: 'Tahoma, Geneva, sans-serif',
    'trebuchet-ms': '"Trebuchet MS", Helvetica, sans-serif',
    monospace: 'ui-monospace, "Courier New", monospace',
  };
  return stacks[choice] || stacks.system;
}

export function resolveInheritedFontStyle(typography) {
  const def = getSiteDefaultFont(typography);
  if (!def) return {};
  return { fontFamily: cssFontFamilyName(def.fontFamily) };
}

export function resolveSiteFontStyle(typography) {
  const t = normalizeSiteTypography(typography);
  if (!t.applyToSite) return {};
  const def = getSiteDefaultFont(t);
  if (!def) return {};
  return { fontFamily: cssFontFamilyName(def.fontFamily) };
}

export function buildSiteFontFaceCss(typography) {
  const fonts = getSiteCustomFonts(typography);
  if (!fonts.length) return '';
  return fonts
    .map((f) => {
      const name = String(f.fontFamily).replace(/'/g, "\\'");
      const url = String(f.fontUrl);
      return `@font-face{font-family:'${name}';src:url('${url}');font-display:swap;}`;
    })
    .join('');
}

/** CSS Quill + aperçu public (.ql-font-*, .web-creator-rich-text). */
export function buildSiteQuillFontCss(typography) {
  const fonts = getSiteCustomFonts(typography);
  const def = getSiteDefaultFont(typography);
  const rules = [];

  if (def) {
    const name = cssFontFamilyName(def.fontFamily);
    rules.push(
      `.ql-font-site-font,.react-quill-editor .ql-font-site-font,.web-creator-rich-text .ql-font-site-font{font-family:${name};}`,
    );
  }

  fonts.forEach((f) => {
    const key = quillFontKeyForCustomFont(f.id);
    const name = cssFontFamilyName(f.fontFamily);
    const label = String(f.label || f.fontFamily).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    rules.push(
      `.ql-font-${key},.react-quill-editor .ql-font-${key},.web-creator-rich-text .ql-font-${key}{font-family:${name};}`,
      `.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${key}"]::before,` +
        `.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${key}"]::before{content:"${label}";}`,
    );
  });

  if (def) {
    const label = String(def.label || def.fontFamily).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    rules.push(
      `.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="site-font"]::before,` +
        `.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="site-font"]::before{content:"${label} (défaut)";}`,
    );
  }

  return rules.join('');
}

export function buildSiteFontsCss(typography) {
  return `${buildSiteFontFaceCss(typography)}${buildSiteQuillFontCss(typography)}`;
}

const TEXT_FONT_TO_QUILL = {
  system: 'sans',
  serif: 'serif',
  arial: 'arial',
  georgia: 'georgia',
  'times-new-roman': 'times-new-roman',
  verdana: 'verdana',
  tahoma: 'tahoma',
  'trebuchet-ms': 'trebuchet-ms',
  monospace: 'monospace',
  site: 'site-font',
};

/** Clé Quill (`ql-font-*`) correspondant à la police du bloc texte. */
export function resolveDefaultQuillFontKey(inheritSiteFont, textFontFamily, typography) {
  if (inheritSiteFont !== false) {
    return getSiteDefaultFont(typography) ? 'site-font' : 'sans';
  }
  const choice = textFontFamily || 'system';
  const customId = parseCustomFontChoice(choice);
  if (customId) return quillFontKeyForCustomFont(customId);
  return TEXT_FONT_TO_QUILL[choice] || 'sans';
}

/** Applique la police du bloc aux paragraphes sans `ql-font-*` (affichage éditeur uniquement). */
export function htmlForQuillEditorDisplay(html, quillFontKey) {
  const raw = String(html || '').trim();
  if (!raw || !quillFontKey || typeof document === 'undefined') return html || '<p><br></p>';
  const className = `ql-font-${quillFontKey}`;
  const root = document.createElement('div');
  root.innerHTML = raw;
  let changed = false;
  root.querySelectorAll('p, h1, h2, h3, h4, li').forEach((el) => {
    if (/\bql-font-/.test(el.className) || el.querySelector('[class*="ql-font-"]')) return;
    el.classList.add(className);
    changed = true;
  });
  return changed ? root.innerHTML : raw;
}

export function buildEditorFontWhitelist(typography) {
  const base = [
    'sans',
    'serif',
    'monospace',
    'arial',
    'georgia',
    'times-new-roman',
    'courier-new',
    'verdana',
    'tahoma',
    'trebuchet-ms',
  ];
  const t = normalizeSiteTypography(typography);
  if (getSiteDefaultFont(t)) base.push('site-font');
  t.customFonts.forEach((f) => base.push(quillFontKeyForCustomFont(f.id)));
  return base;
}

export function buildFontFamilySelectOptions(typography, { includeSiteDefault = true } = {}) {
  const options = [
    { value: 'system', label: 'Système (sans-serif)' },
    { value: 'serif', label: 'Serif (Georgia…)' },
    { value: 'arial', label: 'Arial' },
    { value: 'georgia', label: 'Georgia' },
    { value: 'times-new-roman', label: 'Times New Roman' },
    { value: 'verdana', label: 'Verdana' },
    { value: 'tahoma', label: 'Tahoma' },
    { value: 'trebuchet-ms', label: 'Trebuchet MS' },
    { value: 'monospace', label: 'Monospace' },
  ];
  if (includeSiteDefault && getSiteDefaultFont(typography)) {
    const def = getSiteDefaultFont(typography);
    options.push({
      value: 'site',
      label: `Police par défaut (${def.label || def.fontFamily})`,
    });
  }
  getSiteCustomFonts(typography).forEach((f) => {
    options.push({
      value: customFontChoiceValue(f.id),
      label: f.label || f.fontFamily,
    });
  });
  return options;
}
